import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { 
  Play, 
  Film, 
  ExternalLink, 
  Copy, 
  Check, 
  Info, 
  Sparkles, 
  AlertTriangle, 
  Bell, 
  Quote, 
  Maximize2,
  X
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

interface RichBulletinRendererProps {
  content: string;
  className?: string;
}

// Helper to extract YouTube ID
function getYouTubeEmbedUrl(url: string): string | null {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) 
    ? `https://www.youtube.com/embed/${match[2]}?autoplay=1&rel=0` 
    : null;
}

// Helper to extract Vimeo ID
function getVimeoEmbedUrl(url: string): string | null {
  const regExp = /(?:vimeo\.com\/|video\/)(\d+)/;
  const match = url.match(regExp);
  return match ? `https://player.vimeo.com/video/${match[1]}?autoplay=1` : null;
}

// Helper to extract Loom ID
function getLoomEmbedUrl(url: string): string | null {
  const regExp = /(?:loom\.com\/share\/|loom\.com\/embed\/)([a-f0-9]+)/;
  const match = url.match(regExp);
  return match ? `https://www.loom.com/embed/${match[1]}` : null;
}

// Helper to check if URL is direct video file
function isDirectVideoUrl(url: string): boolean {
  return /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url);
}

// Helper to check if URL is any supported video link
function parseVideoInfo(url: string): { embedUrl?: string; isDirect?: boolean; type: "youtube" | "vimeo" | "loom" | "direct" | "unknown" } | null {
  if (!url) return null;
  
  const yt = getYouTubeEmbedUrl(url);
  if (yt) return { embedUrl: yt, type: "youtube" };
  
  const vimeo = getVimeoEmbedUrl(url);
  if (vimeo) return { embedUrl: vimeo, type: "vimeo" };

  const loom = getLoomEmbedUrl(url);
  if (loom) return { embedUrl: loom, type: "loom" };

  if (isDirectVideoUrl(url)) return { embedUrl: url, isDirect: true, type: "direct" };

  return null;
}

export function RichBulletinRenderer({ content, className }: RichBulletinRendererProps) {
  const [activeImageModal, setActiveImageModal] = useState<{ src: string; alt?: string } | null>(null);
  const [playingVideo, setPlayingVideo] = useState<string | null>(null);
  const [copiedCodeIndex, setCopiedCodeIndex] = useState<number | null>(null);

  const handleCopyCode = (codeText: string, index: number) => {
    navigator.clipboard.writeText(codeText);
    setCopiedCodeIndex(index);
    toast.success("Código copiado al portapapeles");
    setTimeout(() => setCopiedCodeIndex(null), 2000);
  };

  return (
    <div className={cn("prose prose-pink dark:prose-invert max-w-none space-y-4", className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Custom Heading Renderers with anchor links and badges
          h1: ({ children }) => (
            <h1 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight mt-8 mb-4 border-b border-slate-100 dark:border-slate-800 pb-3 flex items-center gap-2">
              <span className="bg-gradient-to-r from-pink-500 to-rose-500 w-2 h-7 rounded-full inline-block mr-1"></span>
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 className="text-xl sm:text-2xl font-extrabold text-slate-800 dark:text-slate-100 mt-6 mb-3 flex items-center gap-2">
              <span className="w-1.5 h-5 bg-pink-500 rounded-full inline-block"></span>
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200 mt-5 mb-2">
              {children}
            </h3>
          ),

          // Custom Paragraph Renderer to handle Video links or syntax
          p: ({ children, node }) => {
            // Check if paragraph contains video syntax or single video link
            const firstChild = node?.children?.[0];
            
            // Handle links inside paragraph
            if (firstChild && firstChild.type === "element" && firstChild.tagName === "a") {
              const href = firstChild.properties?.href as string;
              const videoInfo = parseVideoInfo(href);

              if (videoInfo) {
                const titleText = (firstChild.children?.[0] as any)?.value || "Reproducir Vídeo Informativo";
                return renderVideoPlayer(href, titleText, videoInfo);
              }
            }

            return <p className="text-slate-600 dark:text-slate-300 leading-relaxed text-base my-3">{children}</p>;
          },

          // Custom Link Renderer
          a: ({ href, children }) => {
            if (!href) return <span>{children}</span>;

            // Check if standalone link is a video link
            const videoInfo = parseVideoInfo(href);
            if (videoInfo) {
              const linkText = typeof children === "string" ? children : "Vídeo explicativo";
              return renderVideoPlayer(href, linkText, videoInfo);
            }

            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 font-bold text-pink-600 hover:text-pink-700 dark:text-pink-400 dark:hover:text-pink-300 underline underline-offset-4 decoration-pink-300 dark:decoration-pink-700 transition-colors"
              >
                {children}
                <ExternalLink className="w-3.5 h-3.5 inline-block opacity-70" />
              </a>
            );
          },

          // Custom Image Renderer with Lightbox Zoom
          img: ({ src, alt, title }) => {
            if (!src) return null;

            // Check if img src is a video thumbnail/link
            const isVideoLink = typeof src === "string" && !src.startsWith("data:") && (src.includes("youtube") || src.includes("vimeo") || src.includes("loom"));
            if (isVideoLink) {
              const videoInfo = parseVideoInfo(src);
              if (videoInfo) {
                return renderVideoPlayer(src, alt || "Ver Vídeo", videoInfo);
              }
            }

            return (
              <div className="my-6 space-y-2 group">
                <div 
                  onClick={() => setActiveImageModal({ src, alt: alt || title })}
                  className="relative overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-800 shadow-md hover:shadow-xl transition-all duration-300 cursor-zoom-in bg-slate-100 dark:bg-slate-900 flex items-center justify-center min-h-[120px]"
                >
                  <img
                    src={src}
                    alt={alt || "Imagen del boletín"}
                    className="w-full h-auto object-contain max-h-[600px] group-hover:scale-101 transition-transform duration-500 rounded-2xl"
                    loading="eager"
                  />
                  <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <span className="bg-white/90 dark:bg-slate-900/90 text-slate-800 dark:text-white px-4 py-2 rounded-xl text-xs font-bold shadow-lg flex items-center gap-2 backdrop-blur-md">
                      <Maximize2 className="w-4 h-4 text-pink-500" />
                      Ampliar Imagen
                    </span>
                  </div>
                </div>
                {(alt || title) && alt !== "Imagen Pegada" && (
                  <p className="text-center text-xs text-slate-400 dark:text-slate-500 font-medium italic">
                    {alt || title}
                  </p>
                )}
              </div>
            );
          },

          // Custom Blockquote Renderer (Renders rich Callouts!)
          blockquote: ({ children }) => {
            // Extract raw string content to check for callout markers
            let rawText = "";
            React.Children.forEach(children, (child) => {
              if (React.isValidElement(child)) {
                rawText += (child.props as any)?.children || "";
              } else if (typeof child === "string") {
                rawText += child;
              }
            });

            // Detect Callout Types
            const isNote = rawText.includes("[!NOTE]") || rawText.toLowerCase().includes("nota:");
            const isTip = rawText.includes("[!TIP]") || rawText.toLowerCase().includes("consejo:") || rawText.toLowerCase().includes("tip:");
            const isWarning = rawText.includes("[!WARNING]") || rawText.toLowerCase().includes("advertencia:") || rawText.toLowerCase().includes("atención:");
            const isImportant = rawText.includes("[!IMPORTANT]") || rawText.toLowerCase().includes("importante:") || rawText.toLowerCase().includes("destacado:");

            // Clean string of callout tags
            const cleanChildren = React.Children.map(children, (child) => {
              if (React.isValidElement(child) && (child.props as any)?.children) {
                const subContent = (child.props as any).children;
                if (typeof subContent === "string") {
                  const cleaned = subContent
                    .replace(/\[!(NOTE|TIP|WARNING|IMPORTANT)\]/g, "")
                    .replace(/^(Nota|Consejo|Tip|Advertencia|Atención|Importante):/gi, "");
                  return React.cloneElement(child, { ...child.props, children: cleaned });
                }
              }
              return child;
            });

            if (isTip) {
              return (
                <div className="my-6 p-5 rounded-2xl bg-emerald-50/80 dark:bg-emerald-950/20 border border-emerald-200/80 dark:border-emerald-800/40 text-emerald-950 dark:text-emerald-200 flex items-start gap-4 shadow-sm">
                  <div className="p-2.5 bg-emerald-100 dark:bg-emerald-900/50 text-emerald-600 dark:text-emerald-400 rounded-xl shrink-0 mt-0.5">
                    <Sparkles className="w-5 h-5" />
                  </div>
                  <div className="space-y-1 text-sm font-medium leading-relaxed">
                    <span className="font-extrabold text-emerald-800 dark:text-emerald-300 block text-xs uppercase tracking-wider">Consejo Pro</span>
                    <div>{cleanChildren}</div>
                  </div>
                </div>
              );
            }

            if (isWarning) {
              return (
                <div className="my-6 p-5 rounded-2xl bg-amber-50/80 dark:bg-amber-950/20 border border-amber-200/80 dark:border-amber-800/40 text-amber-950 dark:text-amber-200 flex items-start gap-4 shadow-sm">
                  <div className="p-2.5 bg-amber-100 dark:bg-amber-900/50 text-amber-600 dark:text-amber-400 rounded-xl shrink-0 mt-0.5">
                    <AlertTriangle className="w-5 h-5" />
                  </div>
                  <div className="space-y-1 text-sm font-medium leading-relaxed">
                    <span className="font-extrabold text-amber-800 dark:text-amber-300 block text-xs uppercase tracking-wider">Atención / Advertencia</span>
                    <div>{cleanChildren}</div>
                  </div>
                </div>
              );
            }

            if (isImportant) {
              return (
                <div className="my-6 p-5 rounded-2xl bg-purple-50/80 dark:bg-purple-950/20 border border-purple-200/80 dark:border-purple-800/40 text-purple-950 dark:text-purple-200 flex items-start gap-4 shadow-sm">
                  <div className="p-2.5 bg-purple-100 dark:bg-purple-900/50 text-purple-600 dark:text-purple-400 rounded-xl shrink-0 mt-0.5">
                    <Bell className="w-5 h-5" />
                  </div>
                  <div className="space-y-1 text-sm font-medium leading-relaxed">
                    <span className="font-extrabold text-purple-800 dark:text-purple-300 block text-xs uppercase tracking-wider">Importante</span>
                    <div>{cleanChildren}</div>
                  </div>
                </div>
              );
            }

            if (isNote) {
              return (
                <div className="my-6 p-5 rounded-2xl bg-blue-50/80 dark:bg-blue-950/20 border border-blue-200/80 dark:border-blue-800/40 text-blue-950 dark:text-blue-200 flex items-start gap-4 shadow-sm">
                  <div className="p-2.5 bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 rounded-xl shrink-0 mt-0.5">
                    <Info className="w-5 h-5" />
                  </div>
                  <div className="space-y-1 text-sm font-medium leading-relaxed">
                    <span className="font-extrabold text-blue-800 dark:text-blue-300 block text-xs uppercase tracking-wider">Nota Informativa</span>
                    <div>{cleanChildren}</div>
                  </div>
                </div>
              );
            }

            // Standard elegant quote
            return (
              <div className="my-6 relative p-6 bg-slate-50 dark:bg-slate-850/60 rounded-2xl border-l-4 border-pink-500 shadow-sm">
                <Quote className="w-8 h-8 text-pink-300 dark:text-pink-900/50 absolute top-4 right-4 pointer-events-none" />
                <div className="italic text-slate-700 dark:text-slate-300 font-medium leading-relaxed">
                  {children}
                </div>
              </div>
            );
          },

          // Custom Code Block Renderer
          code: ({ node, inline, className, children, ...props }: any) => {
            const match = /language-(\w+)/.exec(className || '');
            const language = match ? match[1] : '';
            const codeString = String(children).replace(/\n$/, '');

            if (!inline && (match || codeString.includes('\n'))) {
              const codeId = Math.random();
              return (
                <div className="my-6 rounded-2xl overflow-hidden border border-slate-800 bg-slate-950 text-slate-100 shadow-xl font-mono text-xs">
                  <div className="flex items-center justify-between px-4 py-2.5 bg-slate-900 border-b border-slate-800 text-slate-400 text-[11px] font-sans font-semibold">
                    <span className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-pink-500 inline-block"></span>
                      {language ? language.toUpperCase() : "CÓDIGO / SCRIPT"}
                    </span>
                    <button
                      onClick={() => handleCopyCode(codeString, codeId as any)}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-md hover:bg-slate-800 text-slate-300 transition-colors"
                    >
                      {copiedCodeIndex === (codeId as any) ? (
                        <>
                          <Check className="w-3.5 h-3.5 text-emerald-400" />
                          <span className="text-emerald-400">Copiado</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-3.5 h-3.5" />
                          <span>Copiar</span>
                        </>
                      )}
                    </button>
                  </div>
                  <pre className="p-4 overflow-x-auto text-slate-200 leading-relaxed font-mono">
                    <code>{codeString}</code>
                  </pre>
                </div>
              );
            }

            return (
              <code className="bg-pink-50 dark:bg-pink-950/40 text-pink-600 dark:text-pink-300 font-mono text-xs px-1.5 py-0.5 rounded-md border border-pink-200/50 dark:border-pink-900/50" {...props}>
                {children}
              </code>
            );
          },

          // Custom Table Renderer
          table: ({ children }) => (
            <div className="my-6 overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm border-collapse">
                  {children}
                </table>
              </div>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-slate-100/80 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-700 text-xs font-black text-slate-700 dark:text-slate-200 uppercase tracking-wider">
              {children}
            </thead>
          ),
          th: ({ children }) => (
            <th className="p-4 font-bold">{children}</th>
          ),
          td: ({ children }) => (
            <td className="p-4 border-t border-slate-100 dark:border-slate-800 text-slate-600 dark:text-slate-300">{children}</td>
          ),
          tr: ({ children }) => (
            <tr className="hover:bg-slate-50/50 dark:hover:bg-slate-850/40 transition-colors">{children}</tr>
          ),

          // Lists
          ul: ({ children }) => (
            <ul className="my-4 space-y-2 list-disc list-inside text-slate-600 dark:text-slate-300 pl-2">
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className="my-4 space-y-2 list-decimal list-inside text-slate-600 dark:text-slate-300 pl-2">
              {children}
            </ol>
          ),
          li: ({ children }) => (
            <li className="leading-relaxed marker:text-pink-500 font-medium">
              <span className="inline">{children}</span>
            </li>
          ),

          // Divider
          hr: () => (
            <hr className="my-8 border-t-2 border-dashed border-slate-200 dark:border-slate-800" />
          )
        }}
      >
        {content}
      </ReactMarkdown>

      {/* Image Lightbox Modal */}
      <Dialog open={!!activeImageModal} onOpenChange={() => setActiveImageModal(null)}>
        <DialogContent className="max-w-5xl bg-black/95 border-none p-2 shadow-2xl flex flex-col items-center justify-center">
          <DialogTitle className="sr-only">Previsualización de Imagen</DialogTitle>
          {activeImageModal && (
            <div className="relative max-h-[85vh] w-full flex flex-col items-center justify-center p-2">
              <button 
                onClick={() => setActiveImageModal(null)}
                className="absolute top-4 right-4 z-50 bg-white/20 hover:bg-white/40 text-white rounded-full p-2 backdrop-blur-md transition-all"
              >
                <X className="w-6 h-6" />
              </button>
              <img 
                src={activeImageModal.src} 
                alt={activeImageModal.alt || "Imagen"} 
                className="max-h-[80vh] w-auto object-contain rounded-xl shadow-2xl"
              />
              {activeImageModal.alt && (
                <p className="text-slate-300 text-sm mt-3 font-medium text-center">
                  {activeImageModal.alt}
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );

  // Helper to render interactive video player frame
  function renderVideoPlayer(url: string, title: string, videoInfo: { embedUrl?: string; isDirect?: boolean; type: string }) {
    const isPlaying = playingVideo === url;

    if (videoInfo.isDirect && videoInfo.embedUrl) {
      return (
        <div key={url} className="my-6 space-y-2">
          <div className="rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-800 shadow-xl bg-black">
            <video 
              controls 
              className="w-full h-auto max-h-[480px]"
              poster="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?q=80&w=1200&auto=format&fit=crop"
            >
              <source src={videoInfo.embedUrl} type="video/mp4" />
              Tu navegador no soporta el reproductor de vídeo.
            </video>
          </div>
          {title && <p className="text-center text-xs text-slate-400 font-semibold italic">{title}</p>}
        </div>
      );
    }

    if (isPlaying && videoInfo.embedUrl) {
      return (
        <div key={url} className="my-6 space-y-2">
          <div className="relative aspect-video w-full rounded-2xl overflow-hidden shadow-2xl border border-slate-200 dark:border-slate-800 bg-black">
            <iframe
              src={videoInfo.embedUrl}
              title={title}
              className="w-full h-full border-0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
          <div className="flex justify-between items-center px-1">
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">{title}</span>
            <button 
              onClick={() => setPlayingVideo(null)}
              className="text-xs text-pink-600 hover:text-pink-700 font-bold"
            >
              Cerrar Reproductor
            </button>
          </div>
        </div>
      );
    }

    // Thumbnail / Interactive Trigger Frame
    return (
      <div 
        key={url}
        onClick={() => setPlayingVideo(url)}
        className="my-6 relative group aspect-video w-full rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-800 shadow-lg cursor-pointer bg-slate-900 flex flex-col justify-between p-6 transition-all duration-300 hover:shadow-2xl hover:border-pink-500/50"
      >
        {/* Background Decorative Graphic */}
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-950 via-slate-900 to-pink-950/40 opacity-90 group-hover:scale-105 transition-transform duration-700" />
        
        {/* Top Badges */}
        <div className="relative z-10 flex items-center justify-between">
          <span className="bg-pink-600 text-white font-extrabold text-[11px] px-3 py-1 rounded-full uppercase tracking-wider shadow-md flex items-center gap-1.5">
            <Film className="w-3.5 h-3.5" />
            {videoInfo.type.toUpperCase()} VÍDEO
          </span>
          <span className="bg-white/10 text-slate-200 font-medium text-xs px-3 py-1 rounded-full backdrop-blur-md border border-white/10">
            Haz clic para reproducir
          </span>
        </div>

        {/* Center Play Icon */}
        <div className="relative z-10 my-auto flex flex-col items-center justify-center space-y-3">
          <div className="w-16 h-16 rounded-full bg-gradient-to-r from-pink-500 to-rose-600 flex items-center justify-center shadow-xl group-hover:scale-110 group-hover:shadow-pink-500/30 transition-all duration-300">
            <Play className="w-7 h-7 text-white fill-white ml-1" />
          </div>
          <h4 className="text-white font-black text-lg sm:text-xl text-center max-w-lg drop-shadow-md group-hover:text-pink-200 transition-colors">
            {title}
          </h4>
        </div>

        {/* Bottom Banner */}
        <div className="relative z-10 flex items-center justify-between border-t border-white/10 pt-3 text-xs text-slate-400 font-medium">
          <span>Demostración en Video Interactivo</span>
          <span className="text-pink-400 group-hover:translate-x-1 transition-transform font-bold flex items-center gap-1">
            Ver vídeo <ExternalLink className="w-3.5 h-3.5" />
          </span>
        </div>
      </div>
    );
  }
}
